import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { TabsContent } from "@/components/ui/tabs";
import { 
  Users, 
  Clock,
  Eye,
  BarChart3,
  Download,
  RefreshCw,
  CalendarClock,
  Search,
  Loader2
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";

import { API, BACKEND_URL, roleLabels } from './mesaiConstants';
import { getRoleTitle } from './mesaiHelpers';
import { normalizeSearchText } from '@/utils/stringUtils';
import { useRoleFlags } from '@/auth/useRoleFlags';

// Excel/PDF export: window.open backend'e gitmeli (proxy'de API boş olduğu için gerçek backend adresi kullanılır)
const getExportBaseUrl = () => {
  if (API && typeof API === 'string' && API.startsWith('http')) return API.replace(/\/+$/, '');
  if (BACKEND_URL && typeof BACKEND_URL === 'string' && BACKEND_URL.startsWith('http')) return BACKEND_URL.replace(/\/+$/, '');
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    /^30\d{2}$/.test(String(window.location.port || ''))
  ) {
    return 'http://localhost:8000';
  }
  return (API || BACKEND_URL || '').replace(/\/+$/, '');
};
const getExportUrl = (format) => (startDate, endDate) => {
  const base = getExportBaseUrl();
  const apiPath = base && !base.endsWith('/api') ? '/api' : '';
  return `${base || ''}${apiPath}/overtime-report/${format}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`;
};

const MesaiRaporuSection = ({
  // State'ler
  reportStartDate,
  setReportStartDate,
  reportEndDate,
  setReportEndDate,
  reportPersonnelSearch,
  setReportPersonnelSearch,
  reportRoleFilter,
  setReportRoleFilter,
  reportSortBy,
  setReportSortBy,
  // Data
  overtimeSummary,
  personnel,
  allPersonnelForCount, // Tüm personel (aktif+pasif) - Toplam Personel sayısı için; yoksa personnel kullanılır
  isBasicRole,
  userRole,
  // Fonksiyonlar
  fetchOvertimeSummary,
  onPersonnelDetailClick,
  personnelId
}) => {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  const personnelListForCount = (allPersonnelForCount && allPersonnelForCount.length > 0) ? allPersonnelForCount : (personnel || []);
  // ClickableNumber helper bileşeni
  const ClickableNumber = ({ value, hours, onClick, colorClass = '' }) => {
    if (value === 0) return <span className="text-gray-400">0</span>;
    return (
      <button 
        onClick={onClick}
        className={`font-semibold hover:underline cursor-pointer ${colorClass}`}
        title={hours ? `${hours} saat` : ''}
      >
        {value}
        {hours > 0 && <span className="text-xs ml-1 opacity-70">({hours}s)</span>}
      </button>
    );
  };

  return (
    <div className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <h3 className="text-lg font-semibold">Fazla Mesai Raporu</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Personel İsmi ile Arama - En başta */}
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Personel Ara:</Label>
                <Input 
                  type="text" 
                  placeholder="İsim yazın..."
                  value={reportPersonnelSearch}
                  onChange={(e) => setReportPersonnelSearch(e.target.value)}
                  className="w-48"
                />
                {reportPersonnelSearch && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setReportPersonnelSearch('')}
                    className="h-8 px-2"
                  >
                    ✕
                  </Button>
                )}
              </div>
              
              <div className="flex items-center gap-2 ml-2 border-l pl-4 min-w-[300px]">
                <Label className="text-sm whitespace-nowrap">Tarih Aralığı:</Label>
                <div className="flex-1">
                  <DateRangePicker
                    startDate={reportStartDate}
                    endDate={reportEndDate}
                    onChange={({ start, end }) => {
                      setReportStartDate(start || '');
                      setReportEndDate(end || '');
                    }}
                    placeholder="Aralık seçin"
                    highlightHolidays
                    manualInput
                  />
                </div>
              </div>
              <Button variant="outline" onClick={fetchOvertimeSummary} disabled={!reportStartDate || !reportEndDate}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Rapor Getir
              </Button>
              
              {/* Export Butonları */}
              {overtimeSummary && reportStartDate && reportEndDate && (
                <div className="flex items-center gap-2 ml-2 border-l pl-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                    onClick={() => window.open(getExportUrl('excel')(reportStartDate, reportEndDate), '_blank')}
                  >
                    📥 Excel
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-red-50 hover:bg-red-100 text-red-700 border-red-300"
                    onClick={() => window.open(getExportUrl('pdf')(reportStartDate, reportEndDate), '_blank')}
                  >
                    📄 PDF
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          {reportStartDate && reportEndDate && /^\d{4}-\d{2}-\d{2}$/.test(reportStartDate) && /^\d{4}-\d{2}-\d{2}$/.test(reportEndDate) ? (
            <div className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
              📊 Rapor Dönemi: {format(parseISO(reportStartDate), 'd MMMM yyyy', { locale: tr })} - {format(parseISO(reportEndDate), 'd MMMM yyyy', { locale: tr })}
              <span className="ml-2 text-blue-600">(Rakamlara tıklayarak detay görüntüleyebilirsiniz)</span>
            </div>
          ) : (
            <div className="text-sm text-orange-600 bg-orange-50 p-2 rounded">
              ⚠️ Lütfen geçerli başlangıç ve bitiş tarihi seçin
            </div>
          )}

          {overtimeSummary && (
            <>
              {/* Statistics Cards - Sadece amir rolleri için */}
              {/* İstatistikler filtrelenmiş veriye göre hesaplanır */}
              {!isBasicRole && (() => {
                // Rol hiyerarşisine göre personelin görünürlüğü (mesai özetindeki kişiler için)
                const isPersonInMyHierarchy = (targetPerson) => {
                  if (!targetPerson) return false;
                  if (isMudur || isGolgeAdmin) return true;
                  if (targetPerson.id === personnelId) return true;
                  if (isFormen) return targetPerson.formen_id === personnelId;
                  const list = personnelListForCount;
                  if (isBolgeMuhendisi) {
                    if (targetPerson.bolge_muhendisi_id === personnelId) return true;
                    const formen = list.find(p => p.id === targetPerson.formen_id);
                    return formen && formen.bolge_muhendisi_id === personnelId;
                  }
                  if (isMudurYardimcisi) {
                    if (targetPerson.mudur_yardimcisi_id === personnelId) return true;
                    const bolgeMuh = list.find(p => p.id === targetPerson.bolge_muhendisi_id);
                    if (bolgeMuh && bolgeMuh.mudur_yardimcisi_id === personnelId) return true;
                    const formen = list.find(p => p.id === targetPerson.formen_id);
                    if (formen) {
                      if (formen.mudur_yardimcisi_id === personnelId) return true;
                      const formenBolgeMuh = list.find(p => p.id === formen.bolge_muhendisi_id);
                      if (formenBolgeMuh && formenBolgeMuh.mudur_yardimcisi_id === personnelId) return true;
                    }
                    return false;
                  }
                  return false;
                };

                // Sistemdeki toplam personel (yetki alanındaki tüm personel - aktif+pasif)
                const personnelInHierarchy = personnelListForCount.filter(p => isPersonInMyHierarchy(p));
                const totalPersonnelInSystem = personnelInHierarchy.length;

                // Bu dönemde mesai kaydı olan personel (raporda görünen)
                const filteredPersonnel = overtimeSummary.personnel_summary?.filter(person => {
                  if (isMudur || isGolgeAdmin) return true;
                  if (isBasicRole) return person.personnel_id === personnelId;
                  if (person.personnel_id === personnelId) return true;
                  const targetPerson = personnelListForCount.find(p => p.id === person.personnel_id);
                  return isPersonInMyHierarchy(targetPerson);
                }).filter(person => {
                  if (!reportPersonnelSearch.trim()) return true;
                  return normalizeSearchText(person.personnel_name).includes(normalizeSearchText(reportPersonnelSearch));
                }) || [];
                
                // Rapor istatistikleri: bu dönemde mesai yapan personel sayısı ve saat/günler
                const totalPersonnelInReport = filteredPersonnel.length;
                const totalDays = filteredPersonnel.reduce((sum, p) => sum + p.total_days, 0);
                const totalHours = filteredPersonnel.reduce((sum, p) => sum + p.total_hours, 0);
                const avgDays = totalPersonnelInReport > 0 ? Math.round(totalDays / totalPersonnelInReport * 10) / 10 : 0;
                const maxDays = totalPersonnelInReport > 0 ? Math.max(...filteredPersonnel.map(p => p.total_days)) : 0;
                const minDays = totalPersonnelInReport > 0 ? Math.min(...filteredPersonnel.map(p => p.total_days)) : 0;
                const maxHours = totalPersonnelInReport > 0 ? Math.max(...filteredPersonnel.map(p => p.total_hours)) : 0;
                const minHours = totalPersonnelInReport > 0 ? Math.min(...filteredPersonnel.map(p => p.total_hours)) : 0;
                const avgHours = totalPersonnelInReport > 0 ? totalHours / totalPersonnelInReport : 1;
                const distributionScore = totalPersonnelInReport > 0 
                  ? Math.round(100 - ((maxHours - minHours) / Math.max(avgHours, 1) * 50))
                  : 100;
                
                return (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Users className="h-8 w-8 text-blue-500" />
                        <div>
                          <p className="text-sm text-gray-500">Toplam Personel</p>
                          <p className="text-2xl font-bold">{totalPersonnelInSystem}</p>
                          {totalPersonnelInReport > 0 && totalPersonnelInReport !== totalPersonnelInSystem && (
                            <p className="text-xs text-gray-500 mt-0.5">Bu dönemde mesai yapan: {totalPersonnelInReport}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <CalendarClock className="h-8 w-8 text-green-500" />
                        <div>
                          <p className="text-sm text-gray-500">Ortalama Gün</p>
                          <p className="text-2xl font-bold">{avgDays}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Clock className="h-8 w-8 text-purple-500" />
                      <div>
                        <p className="text-sm text-gray-500">Toplam Saat</p>
                        <p className="text-2xl font-bold">{totalHours}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="h-8 w-8 text-orange-500" />
                      <div>
                        <p className="text-sm text-gray-500">Max / Min Gün</p>
                        <p className="text-2xl font-bold">
                          {maxDays} / {minDays}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
                );
              })()}

              {/* Personnel Summary Table - GÜN + SAAT */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    {isBasicRole ? 'Kişisel Mesai Özeti (Gün / Saat)' : 'Personel Bazlı Mesai Özeti (Gün / Saat)'}
                  </CardTitle>
                  {/* 3 Bölümlü Filtre - Sadece amir rolleri için */}
                  {!isBasicRole && (
                    <div className="flex flex-wrap gap-3 mt-3">
                      {/* ROL Filtresi - Bölge Mühendisi / Formen seçimi */}
                      <div className="flex-1 min-w-[200px]">
                        <Label className="text-xs text-gray-500 mb-1 block">Rol / Sorumlu</Label>
                        <SearchableSelect
                          value={reportRoleFilter}
                          onChange={setReportRoleFilter}
                          allLabel="Tümü (Kendime Bağlı)"
                          allValue="all"
                          searchPlaceholder="Rol / sorumlu ara..."
                          options={[
                            // Bölge Mühendisleri
                            ...personnel
                              .filter(p => p.role === 'bolge_muhendisi')
                              .filter(p => {
                                if (isMudur || isGolgeAdmin) return true;
                                if (isMudurYardimcisi) return p.mudur_yardimcisi_id === personnelId;
                                return false;
                              })
                              .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                              .map(p => ({ id: `bm_${p.id}`, name: `🔵 ${p.name} (Bölge Müh.)` })),
                            // Formenler
                            ...personnel
                              .filter(p => p.role === 'formen')
                              .filter(p => {
                                if (isMudur || isGolgeAdmin) return true;
                                if (isMudurYardimcisi) return p.mudur_yardimcisi_id === personnelId;
                                if (isBolgeMuhendisi) return p.bolge_muhendisi_id === personnelId;
                                return false;
                              })
                              .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                              .map(p => ({ id: `f_${p.id}`, name: `🟢 ${p.name} (Formen)` })),
                          ]}
                        />
                      </div>
                      
                      {/* Personel Arama */}
                      <div className="flex-1 min-w-[200px]">
                        <Label className="text-xs text-gray-500 mb-1 block">Personel Ara</Label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            placeholder="İsim ile ara..."
                            value={reportPersonnelSearch}
                            onChange={(e) => setReportPersonnelSearch(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      
                      {/* Sıralama */}
                      <div className="min-w-[180px]">
                        <Label className="text-xs text-gray-500 mb-1 block">Sıralama</Label>
                        <SearchableSelect
                          value={reportSortBy}
                          onChange={setReportSortBy}
                          showAllOption={false}
                          placeholder="Sıralama"
                          searchPlaceholder="Sıralama ara..."
                          options={[
                            { id: "hours_desc", name: "Saat (Yüksek → Düşük)" },
                            { id: "hours_asc", name: "Saat (Düşük → Yüksek)" },
                            { id: "name_asc", name: "İsim (A → Z)" },
                          ]}
                        />
                      </div>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {/* Personnel listesi yüklenene kadar bekle (müdür ve gölge admin hariç) */}
                  {personnel.length === 0 && !isMudur && !isGolgeAdmin ? (
                    <div className="text-center py-8 text-gray-500">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                      Personel verileri yükleniyor...
                    </div>
                  ) : !overtimeSummary?.personnel_summary ? (
                    <div className="text-center py-8 text-gray-500">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                      Mesai özeti yükleniyor...
                    </div>
                  ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-2 px-3">Personel</th>
                          <th className="text-center py-2 px-3 bg-blue-50">
                            <div>Cumartesi</div>
                            <div className="text-xs font-normal text-gray-500">Gün / Saat</div>
                          </th>
                          <th className="text-center py-2 px-3 bg-blue-50">
                            <div>Pazar</div>
                            <div className="text-xs font-normal text-gray-500">Gün / Saat</div>
                          </th>
                          <th className="text-center py-2 px-3 bg-red-50">
                            <div>Resmi Tatil</div>
                            <div className="text-xs font-normal text-gray-500">Gün / Saat</div>
                          </th>
                          <th className="text-center py-2 px-3 bg-purple-50">
                            <div>İdari Tatil</div>
                            <div className="text-xs font-normal text-gray-500">Gün / Saat</div>
                          </th>
                          <th className="text-center py-2 px-3 bg-orange-50">
                            <div>Akşam</div>
                            <div className="text-xs font-normal text-gray-500">Gün / Saat</div>
                          </th>
                          <th className="text-center py-2 px-3 bg-indigo-50">
                            <div>Gece</div>
                            <div className="text-xs font-normal text-gray-500">Gün / Saat</div>
                          </th>
                          <th className="text-center py-2 px-3 bg-green-50 font-bold">Toplam Gün</th>
                          <th className="text-center py-2 px-3 bg-green-50 font-bold">Toplam Saat</th>
                          <th className="text-center py-2 px-3 bg-gray-100">
                            <div>İptal</div>
                            <div className="text-xs font-normal text-gray-500">Gün / Saat</div>
                          </th>
                          <th className="text-center py-2 px-3">Detay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Rol bazlı hiyerarşik filtreleme:
                            - park_gorevlisi/cim_ekibi: Sadece kendisi
                            - formen: Kendine bağlı personel + çim ekibi
                            - bolge_muhendisi: Kendine bağlı formen + personel + çim ekibi
                            - mudur_yardimcisi: Kendine bağlı bölge müh + formen + personel + çim ekibi
                            - mudur: Herkes
                        */}
                        {overtimeSummary.personnel_summary
                          ?.filter(person => {
                            // Müdür / gölge admin: Herkesi görür
                            if (isMudur || isGolgeAdmin) return true;
                            
                            // Temel roller: Sadece kendisi
                            if (isBasicRole) return person.personnel_id === personnelId;
                            
                            // Kendi verisi her zaman görünür
                            if (person.personnel_id === personnelId) return true;
                            
                            // Bu kişinin bilgisini personnel listesinden bul
                            const targetPerson = personnel.find(p => p.id === person.personnel_id);
                            if (!targetPerson) return false;
                            
                            // Formen: Kendine bağlı personel
                            if (isFormen) {
                              return targetPerson.formen_id === personnelId;
                            }
                            
                            // Bölge Mühendisi: Kendine bağlı formen + personel
                            if (isBolgeMuhendisi) {
                              // Doğrudan bağlı formen
                              if (targetPerson.bolge_muhendisi_id === personnelId) return true;
                              // Formene bağlı personel
                              const formen = personnel.find(p => p.id === targetPerson.formen_id);
                              if (formen && formen.bolge_muhendisi_id === personnelId) return true;
                              return false;
                            }
                            
                            // Müdür Yardımcısı: Kendine bağlı bölge müh + formen + personel
                            if (isMudurYardimcisi) {
                              // Doğrudan bağlı bölge mühendisi
                              if (targetPerson.mudur_yardimcisi_id === personnelId) return true;
                              // Bölge mühendisine bağlı formen/personel
                              const bolgeMuh = personnel.find(p => p.id === targetPerson.bolge_muhendisi_id);
                              if (bolgeMuh && bolgeMuh.mudur_yardimcisi_id === personnelId) return true;
                              // Formene bağlı personel
                              const formen = personnel.find(p => p.id === targetPerson.formen_id);
                              if (formen) {
                                if (formen.mudur_yardimcisi_id === personnelId) return true;
                                const formenBolgeMuh = personnel.find(p => p.id === formen.bolge_muhendisi_id);
                                if (formenBolgeMuh && formenBolgeMuh.mudur_yardimcisi_id === personnelId) return true;
                              }
                              return false;
                            }
                            
                            return false;
                          })
                          // Rol filtresi (bölge mühendisi / formen seçimi)
                          .filter(person => {
                            if (reportRoleFilter === 'all') return true;
                            
                            const targetPerson = personnel.find(p => p.id === person.personnel_id);
                            if (!targetPerson) return false;
                            
                            // Bölge mühendisi filtresi (bm_xxx)
                            if (reportRoleFilter.startsWith('bm_')) {
                              const bmId = reportRoleFilter.replace('bm_', '');
                              // Bölge mühendisinin kendisi
                              if (person.personnel_id === bmId) return true;
                              // Bölge mühendisine bağlı formenler
                              if (targetPerson.bolge_muhendisi_id === bmId) return true;
                              // Bölge mühendisine bağlı formenlerin personeli
                              const formen = personnel.find(p => p.id === targetPerson.formen_id);
                              if (formen && formen.bolge_muhendisi_id === bmId) return true;
                              return false;
                            }
                            
                            // Formen filtresi (f_xxx)
                            if (reportRoleFilter.startsWith('f_')) {
                              const formenId = reportRoleFilter.replace('f_', '');
                              // Formenin kendisi
                              if (person.personnel_id === formenId) return true;
                              // Formene bağlı personel
                              if (targetPerson.formen_id === formenId) return true;
                              return false;
                            }
                            
                            return true;
                          })
                          // Personel ismi ile arama filtresi
                          .filter(person => {
                            if (!reportPersonnelSearch.trim()) return true;
                            return normalizeSearchText(person.personnel_name).includes(normalizeSearchText(reportPersonnelSearch));
                          })
                          // Sıralama
                          .sort((a, b) => {
                            if (reportSortBy === 'hours_desc') {
                              return (b.total_hours || 0) - (a.total_hours || 0);
                            } else if (reportSortBy === 'hours_asc') {
                              return (a.total_hours || 0) - (b.total_hours || 0);
                            } else if (reportSortBy === 'name_asc') {
                              return String(a.personnel_name || '').localeCompare(String(b.personnel_name || ''), 'tr');
                            }
                            return 0;
                          })
                          .map((person, index) => (
                          <tr key={person.personnel_id} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                            <td className="py-2 px-3 font-medium">{person.personnel_name}</td>
                            <td className="text-center py-2 px-3 bg-blue-50/50">
                              <ClickableNumber 
                                value={person.cumartesi_count} 
                                hours={person.cumartesi_hours}
                                onClick={() => onPersonnelDetailClick(person, 'cumartesi')}
                                colorClass="text-blue-600"
                              />
                            </td>
                            <td className="text-center py-2 px-3 bg-blue-50/50">
                              <ClickableNumber 
                                value={person.pazar_count} 
                                hours={person.pazar_hours}
                                onClick={() => onPersonnelDetailClick(person, 'pazar')}
                                colorClass="text-blue-600"
                              />
                            </td>
                            <td className="text-center py-2 px-3 bg-red-50/50">
                              <ClickableNumber 
                                value={person.resmi_tatil_count} 
                                hours={person.resmi_tatil_hours}
                                onClick={() => onPersonnelDetailClick(person, 'resmi_tatil')}
                                colorClass="text-red-600"
                              />
                            </td>
                            <td className="text-center py-2 px-3 bg-purple-50/50">
                              <ClickableNumber 
                                value={person.idari_tatil_count || 0} 
                                hours={person.idari_tatil_hours || 0}
                                onClick={() => onPersonnelDetailClick(person, 'idari_tatil')}
                                colorClass="text-purple-600"
                              />
                            </td>
                            <td className="text-center py-2 px-3 bg-orange-50/50">
                              <ClickableNumber 
                                value={person.aksam_mesaisi_count || 0} 
                                hours={person.aksam_mesaisi_hours || 0}
                                onClick={() => onPersonnelDetailClick(person, 'aksam_mesaisi')}
                                colorClass="text-orange-600"
                              />
                            </td>
                            <td className="text-center py-2 px-3 bg-indigo-50/50">
                              <ClickableNumber 
                                value={person.gece_mesaisi_count || 0} 
                                hours={person.gece_mesaisi_hours || 0}
                                onClick={() => onPersonnelDetailClick(person, 'gece_mesaisi')}
                                colorClass="text-indigo-600"
                              />
                            </td>
                            <td className="text-center py-2 px-3 font-bold bg-green-50/50">
                              <ClickableNumber 
                                value={person.total_days} 
                                onClick={() => onPersonnelDetailClick(person, 'all')}
                                colorClass="text-green-700"
                              />
                            </td>
                            <td className="text-center py-2 px-3 font-bold bg-green-50/50 text-green-700">
                              {person.total_hours}
                            </td>
                            <td className="text-center py-2 px-3 bg-gray-100/50">
                              {(person.cancelled_count || 0) > 0 ? (
                                <span className="text-gray-600 font-medium">
                                  {person.cancelled_count} ({person.cancelled_hours}s)
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="text-center py-2 px-3">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => onPersonnelDetailClick(person, 'all')}
                              >
                                <Eye className="h-4 w-4 text-gray-500" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {/* Temel roller için veri bulunamadı mesajı */}
                        {isBasicRole && overtimeSummary.personnel_summary?.filter(p => p.personnel_id === personnelId).length === 0 && (
                          <tr>
                            <td colSpan={11} className="text-center py-8 text-gray-500">
                              Seçilen tarih aralığında sizin için mesai kaydı bulunamadı
                            </td>
                          </tr>
                        )}
                        {/* Amir rolleri için veri bulunamadı mesajı */}
                        {!isBasicRole && (!overtimeSummary.personnel_summary || overtimeSummary.personnel_summary.length === 0) && (
                          <tr>
                            <td colSpan={11} className="text-center py-8 text-gray-500">
                              Seçilen tarih aralığında mesai kaydı bulunamadı
                            </td>
                          </tr>
                        )}
                        {/* Arama sonucu bulunamadı mesajı */}
                        {!isBasicRole && overtimeSummary.personnel_summary?.length > 0 && 
                         reportPersonnelSearch.trim() && 
                         overtimeSummary.personnel_summary.filter(p => 
                           normalizeSearchText(p.personnel_name).includes(normalizeSearchText(reportPersonnelSearch))
                         ).length === 0 && (
                          <tr>
                            <td colSpan={11} className="text-center py-8 text-orange-500">
                              "{reportPersonnelSearch}" aramasına uygun personel bulunamadı
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
    </div>
  );
};

export default MesaiRaporuSection;
